import React, { useEffect, useRef } from 'react'
import { RouteComponentProps, useParams } from 'react-router-dom'
import { useQuery } from 'react-query'
import BackButton from '../../components/AppBackButton/BackButton'
import MainLayout from '../../components/TheMainLayout/MainLayout'
import { OnlineMockExamCourseLesson } from '../../services/types'
import Video from '../../components/AppVideo/Video'
import ButtonSelect from '../../components/AppButtonSelect/ButtonSelect'
import Icon from '../../components/AppIcon/Icon'
import Loader from '../../components/AppLoader/Loader'
import './OnlineMockExamCourse.scss'
import {
  getOnlineMockExamCourseLesson,
  submitOnlineMockExamCourseLesson
} from '../../moduls/online-mock-exam-courses'
import FormInputFile from '../../components/Forms/Inputs/FormInputFile'
import { Form, Formik } from 'formik'
import * as Yup from 'yup'
import Button from '../../components/AppButton/Button'
import { GlobalTypes, useGlobalState } from '../../contexts/global'

interface UploadedFile {
  file: File
  data: string
}

interface FormValues {
  files: UploadedFile[]
}

const initialValues: FormValues = {
  files: []
}

const OnlineMockExamCourseLessonDetail: React.FC<RouteComponentProps> = (props) => {
  const { courseId, lessonId } = useParams<{ courseId: string; lessonId: string }>()
  const [parentPassword, setParentPassword] = React.useState<string | null>(null)
  const { state, dispatch } = useGlobalState()
  const {
    data: courseLesson,
    isFetched,
    refetch
  } = useQuery<OnlineMockExamCourseLesson>(
    ['online-course-lesson', { id: lessonId, parentPassword: parentPassword }],
    getOnlineMockExamCourseLesson
  )

  const refH2 = useRef<any>(null)

  useEffect(function () {
    refH2.current?.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' })
  })

  if (!isFetched) return <Loader />

  const partsIds: number[] = []

  let scrollIdFromUrl: string = window.location.hash.replace('#', '')

  courseLesson?.interpretation_parts.forEach((part) => {
    partsIds.push(part.id)
  })

  let scrollId: number =
    partsIds[partsIds.indexOf(+scrollIdFromUrl) + 1] ?? partsIds[partsIds.indexOf(+scrollIdFromUrl)]

  const validationSchema = Yup.object({
    files: Yup.array()
      .min(1, 'Povinné pole')
      .of(
        Yup.object({
          file: Yup.mixed<File>()
            .required('Soubor je povinný')
            .test(
              'fileSize',
              'Maximální velikost souboru je 10 MB',
              (f) => !!f && f.size <= 10 * 1024 * 1024
            )
            .test(
              'fileType',
              'Povolené formáty jsou PDF, PNG, JPEG',
              (f) => !!f && ['application/pdf', 'image/png', 'image/jpeg'].includes(f.type)
            ),
          data: Yup.string().required()
        })
      )
  })

  return (
    <>
      <MainLayout>
        {courseLesson?.interpretation_parts.map((part) => (
          <div key={part.id} className="online-mock-exam-course-interpretation">
            <div className="h-text-center">
              <div
                ref={part.id === +scrollId ? refH2 : null}
                style={{ display: 'hide', height: '5rem' }}
              ></div>
              <h2>{part.name}</h2>
              {part.videos.map((video) =>
                video.url ? (
                  <Video url={video.url} className="h-mb-2" />
                ) : (
                  <ButtonSelect
                    onClick={() => {
                      dispatch({
                        type: GlobalTypes.ToggleModal,
                        modal: 'ModalEnterParentPassword',
                        props: {
                          callback: (password: string) => {
                            setParentPassword(password)
                          }
                        }
                      })
                    }}
                    rightPart={<Icon icon="lock-gray" width="1.25rem" height="1.25rem" />}
                    isLocked={true}
                    className="is-tall"
                  >
                    Zamčené video
                  </ButtonSelect>
                )
              )}
            </div>
            <div className="h-mt-2">
              {part.files.map((file, idx) => (
                <a
                  key={idx}
                  href={file.fullPath}
                  target="_blank"
                  rel="noopener noreferrer"
                  download
                  className="h-display-block h-mb-075"
                >
                  <ButtonSelect
                    onClick={() => {
                      if (!file.fullPath) {
                        dispatch({
                          type: GlobalTypes.ToggleModal,
                          modal: 'ModalEnterParentPassword',
                          props: {
                            callback: (password: string) => {
                              setParentPassword(password)
                            }
                          }
                        })
                      }
                    }}
                    rightPart={
                      <Icon
                        icon={!!file.fullPath ? 'download-primary' : 'lock-gray'}
                        width="1.25rem"
                        height="1.25rem"
                      />
                    }
                    isLocked={!file.fullPath}
                  >
                    {file.name}
                  </ButtonSelect>
                </a>
              ))}
            </div>
          </div>
        ))}
        <div className="h-mt-2">
          <h2 className="h-text-center">Odevzdání</h2>
          {courseLesson?.submissions?.length ? (
            <div className="container-row">
              <div className="col-lg-4">
                {courseLesson.submissions[0].corrected_test_file ? (
                  <p className="h-font-size-125">
                    <strong>Dosažené skóre:</strong>
                    <span className="h-px-1 h-py-05 h-ml-1 h-background-secondary h-color-white h-border-radius">
                      {courseLesson.submissions[0].score}/50
                    </span>
                  </p>
                ) : (
                  <p className="h-px-15 h-py-1 h-background-success h-color-white h-border-radius">
                    <b>Test byl úspěšně odevzdán!</b>
                    <br />
                    Jakmile bude opraven, dostanete e-mail notifikaci
                  </p>
                )}
              </div>
              <div className="col-lg-8">
                {courseLesson.submissions[0].submitted_test_files.length > 0 &&
                  courseLesson.submissions[0].submitted_test_files.map((v, i) => (
                    <a
                      key={i}
                      href={v.fullPath}
                      target="_blank"
                      rel="noopener noreferrer"
                      download
                      className="h-display-block h-mb-1"
                    >
                      <ButtonSelect
                        onClick={() => {}}
                        rightPart={
                          <Icon icon="download-primary" width="1.25rem" height="1.25rem" />
                        }
                      >
                        Odevzdaný test
                      </ButtonSelect>
                    </a>
                  ))}
                {courseLesson.submissions[0].corrected_test_file && (
                  <a
                    href={courseLesson.submissions[0].corrected_test_file.fullPath}
                    target="_blank"
                    rel="noopener noreferrer"
                    download
                    className="h-display-block h-mt-1"
                  >
                    <ButtonSelect
                      onClick={() => {}}
                      rightPart={<Icon icon="download-primary" width="1.25rem" height="1.25rem" />}
                      className="is-correct"
                    >
                      Opravený test
                    </ButtonSelect>
                  </a>
                )}
              </div>
            </div>
          ) : (
            <Formik<FormValues>
              initialValues={initialValues}
              onSubmit={(values: FormValues, actions) => {
                const selectedFiles = (values.files ?? [])
                  .filter((f): f is UploadedFile => !!f && !!f.file)
                  .map((f) => f.file)

                if (selectedFiles.length === 0) {
                  return
                }

                submitOnlineMockExamCourseLesson(+lessonId, selectedFiles)
                  .then(() => {
                    actions.setSubmitting(false)
                    actions.resetForm()
                    refetch()

                    dispatch({
                      type: GlobalTypes.ToggleNotification,
                      style: 'success',
                      text: 'Test byl úspěšně odevzdán.'
                    })
                  })
                  .catch((error) => {
                    actions.setSubmitting(false)
                    dispatch({
                      type: GlobalTypes.ToggleNotification,
                      style: 'error',
                      text: error?.response?.data?.message
                    })
                  })
              }}
              validationSchema={validationSchema}
            >
              {({ isValid, dirty, isSubmitting }) => (
                <Form>
                  <FormInputFile
                    attributes={{ help: 'PNG, JPEG nebo PDF (max. 10 MB)', multiple: true }}
                    name="files"
                  />
                  <div className="h-text-center h-mt-1">
                    <Button
                      type="submit"
                      color="primary"
                      disabled={!(isValid && dirty)}
                      loading={isSubmitting}
                    >
                      Odevzdat
                    </Button>
                  </div>
                </Form>
              )}
            </Formik>
          )}
        </div>
      </MainLayout>
      <BackButton />
    </>
  )
}

export default OnlineMockExamCourseLessonDetail
